import { ChevronDownIcon, ChevronRightIcon } from '@heroicons/react/24/outline';
import { useEffect, useMemo, useRef, useState } from 'react';
import { MetadataEntry } from '../../types/MetadataEntry';
import { parseJsonMetadata, serializeJsonMetadata } from '../../utils/jsonMetadata';
import { buildMetadataPreview, countMetadataFields } from '../../utils/metadataPreview';
import { FieldHelp } from '../ui/FieldHelp';
import { JsonMetadataModal } from './JsonMetadataModal';
import { MetadataEditor } from './MetadataEditor';

type JsonMetadataFieldProps = {
  label: string;
  description?: string;
  helpDescription?: string;
  helpExample?: string;
  value?: string | null;
  onChange: (json: string) => void;
  disabled?: boolean;
  defaultExpanded?: boolean;
};

function normalizeJsonValue(json?: string | null): string {
  return json?.trim() ? json : '';
}

export function JsonMetadataField({
  label,
  description,
  helpDescription,
  helpExample,
  value,
  onChange,
  disabled = false,
  defaultExpanded = false,
}: JsonMetadataFieldProps) {
  const [entries, setEntries] = useState<MetadataEntry[]>(() => parseJsonMetadata(value));
  const [expanded, setExpanded] = useState(defaultExpanded);
  const [modalOpen, setModalOpen] = useState(false);
  const lastEmittedJsonRef = useRef(normalizeJsonValue(value));

  useEffect(() => {
    const normalized = normalizeJsonValue(value);
    if (normalized === lastEmittedJsonRef.current) {
      return;
    }

    lastEmittedJsonRef.current = normalized;
    setEntries(parseJsonMetadata(value));
  }, [value]);

  const fieldCount = useMemo(() => countMetadataFields(value), [value]);
  const previewLines = useMemo(() => buildMetadataPreview(value), [value]);

  function applyEntries(nextEntries: MetadataEntry[]): void {
    setEntries(nextEntries);
    const json = serializeJsonMetadata(nextEntries);
    lastEmittedJsonRef.current = json;
    onChange(json);
  }

  return (
    <div className="rounded-xl border border-secondary-200 bg-secondary-50/40">
      <div className="flex flex-wrap items-start justify-between gap-3 p-3">
        <div className="flex min-w-0 flex-1 items-start gap-2">
          <button
            type="button"
            className="flex min-w-0 flex-1 items-start gap-2 text-left"
            onClick={() => setExpanded((current) => !current)}
          >
            {expanded ? (
              <ChevronDownIcon className="mt-0.5 h-4 w-4 shrink-0 text-secondary-500" />
            ) : (
              <ChevronRightIcon className="mt-0.5 h-4 w-4 shrink-0 text-secondary-500" />
            )}
            <span className="min-w-0">
              <span className="flex items-center gap-1 text-sm font-medium text-secondary-900">
                {label}
              </span>
              {description ? (
                <span className="mt-0.5 block text-xs text-secondary-500">{description}</span>
              ) : null}
              {!expanded ? (
                <span className="mt-1 block text-xs text-secondary-600">
                  {fieldCount > 0
                    ? `${fieldCount} campo(s) definido(s)${previewLines.length ? `: ${previewLines.join(' · ')}` : ''}`
                    : 'Nenhum metadado definido'}
                </span>
              ) : null}
            </span>
          </button>
          {helpDescription ? (
            <span className="mt-0.5 shrink-0">
              <FieldHelp description={helpDescription} example={helpExample} />
            </span>
          ) : null}
        </div>

        <div className="erp-btn-actions shrink-0">
          <button
            type="button"
            className="erp-btn-secondary erp-btn-sm"
            disabled={disabled}
            onClick={() => setModalOpen(true)}
          >
            Abrir editor
          </button>
        </div>
      </div>

      {expanded ? (
        <div className="border-t border-secondary-200 bg-white px-3 py-3">
          <MetadataEditor entries={entries} disabled={disabled} onChange={applyEntries} />
        </div>
      ) : null}

      <JsonMetadataModal
        open={modalOpen}
        title={label}
        description={description}
        entries={entries}
        disabled={disabled}
        onChange={applyEntries}
        onClose={() => setModalOpen(false)}
      />
    </div>
  );
}
